import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { Toaster } from './components/ui/sonner';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { toast } from 'sonner';
import CapacitorService from './services/CapacitorService';
import storage from './utils/storage';
import logger from './utils/logger';
import {
  FeedSkeleton,
  TripsSkeleton,
  ChatListSkeleton,
  ChatRoomSkeleton,
  ProfileSkeleton,
  NotificationsSkeleton,
  GenericPageSkeleton,
} from './components/ui/skeletons';

// ---------- Lazy-loaded routes ----------
// Each route becomes its own webpack chunk; the initial bundle drops dramatically.
// We add a small retry wrapper so a transient chunk-load failure on flaky
// mobile networks doesn't leave the user stuck on a blank screen.
function lazyWithRetry(factory, retries = 2) {
  return lazy(() =>
    factory().catch(async (err) => {
      for (let i = 0; i < retries; i++) {
        await new Promise((r) => setTimeout(r, 500 * (i + 1)));
        try { return await factory(); } catch (e) { /* keep retrying */ }
      }
      throw err;
    })
  );
}

const AuthPage = lazyWithRetry(() => import('./components/AuthPage'));
const PrivacyPolicyPage = lazyWithRetry(() => import('./components/PrivacyPolicyPage'));
const TermsOfServicePage = lazyWithRetry(() => import('./components/TermsOfServicePage'));
const HomePage = lazyWithRetry(() => import('./components/HomePage'));
const TripsPage = lazyWithRetry(() => import('./components/TripsPage'));
const ConnectionsPage = lazyWithRetry(() => import('./components/ConnectionsPage'));
const MapPage = lazyWithRetry(() => import('./components/MapPage'));
const AIPlannerPage = lazyWithRetry(() => import('./components/AIPlannerPage'));
const ExplorePage = lazyWithRetry(() => import('./components/ExplorePage'));
const DestinationPage = lazyWithRetry(() => import('./components/DestinationPage'));
const NearbyPage = lazyWithRetry(() => import('./components/NearbyPage'));
const TripPlannerPage = lazyWithRetry(() => import('./components/TripPlannerPage'));
const CreateTripWizard = lazyWithRetry(() => import('./components/CreateTripWizard'));
const TripDetailPage = lazyWithRetry(() => import('./components/TripDetailPage'));
const ProfilePage = lazyWithRetry(() => import('./components/ProfilePage'));
const AdminLogin = lazyWithRetry(() => import('./components/AdminLogin'));
const AdminDashboard = lazyWithRetry(() => import('./components/AdminDashboard'));
const AuthenticatedLayout = lazyWithRetry(() => import('./components/AuthenticatedLayout'));
const ConnectionChatPage = lazyWithRetry(() => import('./components/ConnectionChatPage'));
const MessagesPage = lazyWithRetry(() => import('./components/MessagesPage'));
const NotificationsPage = lazyWithRetry(() => import('./components/NotificationsPage'));
const BottomNav = lazyWithRetry(() => import('./components/BottomNav'));
const ServiceDetailPage = lazyWithRetry(() => import('./components/ServiceDetailPage'));
const BookingManagementPage = lazyWithRetry(() => import('./components/BookingManagementPage'));
const ServiceProviderDashboard = lazyWithRetry(() => import('./components/ServiceProviderDashboard'));
const InvitationsPage = lazyWithRetry(() => import('./components/InvitationsPage'));
const TripActivitiesPage = lazyWithRetry(() => import('./components/TripActivitiesPage'));
const ScrollToTop = lazyWithRetry(() => import('./components/ScrollToTop'));
const ProfileSetup = lazyWithRetry(() => import('./components/ProfileSetup'));
const PublicTripsPage = lazyWithRetry(() => import('./components/PublicTripsPage'));
const FeedPage = lazyWithRetry(() => import('./components/FeedPage'));
const ServiceProviderOnboarding = lazyWithRetry(() => import('./components/ServiceProviderOnboarding'));
const PublicProfilePage = lazyWithRetry(() => import('./components/PublicProfilePage'));
const PublicTripDetailPage = lazyWithRetry(() => import('./components/PublicTripDetailPage'));
const Dashboard = lazyWithRetry(() => import('./components/Dashboard'));
const SplitsPage = lazyWithRetry(() => import('./components/SplitsPage'));
const ExperienceDetailPage = lazyWithRetry(() => import('./components/ExperienceDetailPage'));

// ---------- Constants ----------
// Mobile-friendly environment variable access
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper: render a route page wrapped in a Suspense fallback skeleton
function withSuspense(node, fallback = <GenericPageSkeleton />) {
  return <Suspense fallback={fallback}>{node}</Suspense>;
}

// ---------- Main route handler ----------
function MainRouteHandler({ needsProfileSetup }) {
  const [loading, setLoading] = useState(true);
  const [shouldSetupProfile, setShouldSetupProfile] = useState(false);
  const [pinnedTripId, setPinnedTripId] = useState(null);

  useEffect(() => {
    const checkRoute = async () => {
      const needsSetup = await needsProfileSetup();
      setShouldSetupProfile(needsSetup);
      const tripId = await storage.getItem('pinnedTripId');
      setPinnedTripId(tripId);
      setLoading(false);
    };
    checkRoute();
  }, [needsProfileSetup]);

  if (loading) return <FeedSkeleton />;
  if (shouldSetupProfile) return <Navigate to="/setup-profile" replace />;
  if (pinnedTripId) return <Navigate to={`/trip/${pinnedTripId}`} replace />;
  return <Navigate to="/home" replace />;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);

  // ---------- Axios setup ----------
  useEffect(() => {
    logger.log('🌐 BACKEND URL:', BACKEND_URL);
    if (BACKEND_URL && BACKEND_URL.includes('4awbhktaqq')) {
      logger.error('🚨 Using OLD backend URL — deployment is stale.');
    }

    axios.defaults.baseURL = API;
    axios.defaults.withCredentials = true;

    const interceptor = axios.interceptors.request.use(async (config) => {
      const token = await storage.getItem('access_token');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    }, (error) => Promise.reject(error));

    return () => { axios.interceptors.request.eject(interceptor); };
  }, []);

  // ---------- Capacitor + auth ----------
  useEffect(() => {
    initializeCapacitor();
    handleAuthentication();
    // Fire-and-forget: Firebase Performance Monitoring on web only.
    import('./utils/firebasePerf').then(({ initFirebasePerf }) => initFirebasePerf()).catch(() => {});
    // Fire-and-forget: Analytics init (never blocks boot)
    import('./utils/analytics').then(({ initAnalytics }) => initAnalytics()).catch(() => {});
    // Background-sync queue: flushes failed mutations when connectivity returns.
    import('./utils/backgroundSync').then(({ registerSyncOnReconnect }) => registerSyncOnReconnect()).catch(() => {});
  }, []);

  const initializeCapacitor = async () => {
    try {
      await CapacitorService.initialize();
      CapacitorService.onNetworkChange((status) => {
        if (!status.connected) {
          toast.warning('You are offline', { description: 'Some features may be limited' });
        } else {
          toast.success('Back online!');
        }
      });
      CapacitorService.onAppStateChange((state) => {
        logger.log('📱 App state:', state.isActive ? 'Active' : 'Background');
      });
    } catch (error) {
      logger.error('❌ Capacitor initialization error:', error);
    }
  };

  // ---------- Push notifications ----------
  useEffect(() => {
    if (isAuthenticated && user) setupPushNotifications();
  }, [isAuthenticated, user]);

  const setupPushNotifications = async () => {
    try {
      if (CapacitorService.isNativePlatform()) {
        const fcmToken = await CapacitorService.initializePushNotifications();
        if (fcmToken) {
          await axios.post('/users/fcm-token', { token: fcmToken });
        }
        CapacitorService.listeners.pushNotification.push((payload) => {
          const title = payload?.title || payload?.notification?.title || 'Notification';
          const body = payload?.body || payload?.notification?.body || '';
          const data = payload?.data || {};
          toast.info(title, {
            description: body,
            duration: 5000,
            action: data?.url ? { label: 'View', onClick: () => { window.location.href = data.url; } } : undefined,
          });
          fetchNotifications();
        });
      } else {
        const { requestNotificationPermission, onMessageListener } = await import('./utils/notifications');
        const fcmToken = await requestNotificationPermission();
        if (fcmToken) {
          await axios.post('/users/fcm-token', { token: fcmToken });
        }
        onMessageListener()
          .then((payload) => {
            const { notification, data } = payload;
            toast.info(notification.title, {
              description: notification.body,
              duration: 5000,
              action: data?.url ? { label: 'View', onClick: () => { window.location.href = data.url; } } : undefined,
            });
            fetchNotifications();
          })
          .catch(() => { /* listener not available */ });
      }
    } catch (error) {
      logger.error('Error setting up push notifications:', error);
    }
  };

  // ---------- Authentication flows ----------
  const handleAuthentication = async () => {
    const { handleRedirectResult } = await import('./config/firebase');
    const redirectResult = await handleRedirectResult();

    if (redirectResult.success && redirectResult.idToken) {
      toast.info('Completing Google sign-in...');
      try {
        const response = await axios.post('/auth/google', { id_token: redirectResult.idToken });
        await storage.setItem('access_token', response.data.access_token);
        await new Promise((resolve) => setTimeout(resolve, 100));
        const profileResponse = await axios.get('/auth/profile', {
          headers: { Authorization: `Bearer ${response.data.access_token}` },
        });
        setUser(profileResponse.data);
        setIsAuthenticated(true);
        await fetchNotifications();
        toast.success('Welcome to RoamMate! 🌍');
        setLoading(false);
        return;
      } catch (error) {
        logger.error('Google auth failed', error?.response?.data);
        toast.error('Failed to complete sign-in');
      }
    }

    const hash = window.location.hash;
    const sessionId = hash.startsWith('#session_id=') ? hash.substring(12) : null;
    if (sessionId) {
      toast.info('Processing Google login...');
      await processOAuthSession(sessionId);
      window.history.replaceState(null, null, window.location.pathname);
      return;
    }

    await checkAuthStatus();
  };

  const processOAuthSession = async (sessionId) => {
    try {
      setLoading(true);
      const response = await axios.post('/auth/session-data', {}, {
        headers: { 'X-Session-ID': sessionId },
        withCredentials: true,
      });
      if (response.data.access_token) {
        await storage.setItem('access_token', response.data.access_token);
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      const userData = response.data.user || response.data;
      setUser(userData);
      setIsAuthenticated(true);
      await fetchNotifications();
      toast.success('Welcome to RoamMate! 🌍');
      setLoading(false);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'OAuth authentication failed. Please try again.';
      toast.error(errorMessage);
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
    }
  };

  const checkAuthStatus = async () => {
    try {
      const response = await axios.get('/auth/profile');
      setUser(response.data);
      setIsAuthenticated(true);
      await fetchNotifications();
      // Fire North Star event: session opened while user has profile (may have active trips)
      import('./utils/analytics').then(({ Analytics }) => Analytics.sessionWithActiveTrip()).catch(() => {});
    } catch (error) {
      await storage.removeItem('access_token');
      setIsAuthenticated(false);
    }
    setLoading(false);
  };

  const needsProfileSetup = async () => {
    if (!user) return false;
    const setupCompleted = await storage.getItem('profile_setup_completed');
    if (setupCompleted === 'true') return false;
    const hasDateOfBirth = user.date_of_birth && user.date_of_birth !== '';
    const hasLocation = user.location && (typeof user.location === 'string' ? user.location !== '' : (user.location.address || user.location.lat));
    const hasBio = user.bio && user.bio !== '';
    const hasInterests = user.interests && user.interests.length > 0;
    return !(hasDateOfBirth || hasLocation || hasBio || hasInterests);
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get('/notifications?limit=10');
      setNotifications(response.data.notifications || []);
    } catch (error) {
      logger.error('Error fetching notifications:', error);
    }
  };

  // Firebase RTDB listener for unread message count
  useEffect(() => {
    if (!user?.user_id) return;
    let cleanup = null;
    import('./utils/firebaseConfig').then(({ database, authenticateFirebase }) => {
      if (!database) return;
      authenticateFirebase().then((authenticated) => {
        if (!authenticated) return;
        import('firebase/database').then(({ ref, onValue, off }) => {
          const convRef = ref(database, `conversations/${user.user_id}`);
          onValue(convRef, (snapshot) => {
            const data = snapshot.val() || {};
            const total = Object.values(data).reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
            setUnreadMessages(total);
          });
          cleanup = () => off(convRef);
        });
      });
    }).catch(() => {});
    return () => { if (cleanup) cleanup(); };
  }, [user?.user_id]);

  const refreshNotifications = () => { fetchNotifications(); };

  const handleLogin = async (email, password) => {
    try {
      const response = await axios.post('/auth/login', { email, password });
      await storage.setItem('access_token', response.data.access_token);
      await new Promise((resolve) => setTimeout(resolve, 100));
      const profileResponse = await axios.get('/auth/profile', {
        headers: { Authorization: `Bearer ${response.data.access_token}` },
      });
      setUser(profileResponse.data);
      setIsAuthenticated(true);
      updateUserLocation(response.data.access_token);
      setupPeriodicLocationUpdates();
      await fetchNotifications();
      toast.success('Welcome back to RoamMate! 🌍');
      return { success: true };
    } catch (error) {
      await storage.removeItem('access_token');
      setIsAuthenticated(false);
      setUser(null);
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const updateUserLocation = async (token = null) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const authToken = token || (await storage.getItem('access_token'));
          await axios.patch('/auth/profile', {
            location: { lat: latitude, lng: longitude, updated_at: new Date().toISOString() },
          }, { headers: { Authorization: `Bearer ${authToken}` } });
        } catch (err) {
          logger.log('⚠️ Location update failed:', err.message);
        }
      },
      () => logger.log('⚠️ Geolocation permission denied or unavailable')
    );
  };

  const setupPeriodicLocationUpdates = () => {
    if (window.locationUpdateInterval) clearInterval(window.locationUpdateInterval);
    window.locationUpdateInterval = setInterval(async () => {
      const token = await storage.getItem('access_token');
      if (token) updateUserLocation();
    }, 1800000);
  };

  useEffect(() => {
    if (isAuthenticated) setupPeriodicLocationUpdates();
    return () => {
      if (window.locationUpdateInterval) clearInterval(window.locationUpdateInterval);
    };
  }, [isAuthenticated]);

  const handleRegister = async (userData) => {
    try {
      await axios.post('/auth/register', userData);
      const loginResult = await handleLogin(userData.email, userData.password);
      if (loginResult.success) toast.success('Account created successfully! Welcome to RoamMate! 🎉');
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const handleLogout = async () => {
    try { await axios.post('/auth/logout'); } catch (error) { /* ignore */ }
    await storage.removeItem('access_token');
    // Drop the API cache so the next user doesn't see stale data
    try {
      const { clearAll } = await import('./utils/apiCache');
      await clearAll();
    } catch (e) { /* ignore */ }
    setIsAuthenticated(false);
    setUser(null);
    setNotifications([]);
    toast.success('See you next adventure! 👋');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-emerald-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" data-testid="app-initial-loader"></div>
          <p className="text-emerald-700 font-medium">Loading RoamMate...</p>
        </div>
      </div>
    );
  }

  // Helper: gate any route on auth + wrap in the AuthenticatedLayout + Suspense
  const authedLayout = (node, fallback = <GenericPageSkeleton />) =>
    isAuthenticated ? (
      <Suspense fallback={fallback}>
        <AuthenticatedLayout user={user} onLogout={handleLogout} notifications={notifications} unreadMessages={unreadMessages}>
          {node}
        </AuthenticatedLayout>
      </Suspense>
    ) : (
      <Navigate to="/auth" replace />
    );

  return (
    <div className="App">
      <Router>
        <Suspense fallback={null}><ScrollToTop /></Suspense>
        <Routes>
          <Route
            path="/auth"
            element={!isAuthenticated
              ? withSuspense(<AuthPage onLogin={handleLogin} onRegister={handleRegister} />)
              : <Navigate to="/" replace />}
          />

          <Route
            path="/setup-profile"
            element={isAuthenticated
              ? withSuspense(<ProfileSetup user={user} onComplete={checkAuthStatus} />)
              : <Navigate to="/auth" replace />}
          />

          <Route
            path="/"
            element={isAuthenticated
              ? <MainRouteHandler needsProfileSetup={needsProfileSetup} />
              : <Navigate to="/auth" replace />}
          />

          <Route path="/home" element={authedLayout(<HomePage user={user} notifications={notifications} />, <FeedSkeleton />)} />
          <Route path="/feed" element={authedLayout(<FeedPage user={user} />, <FeedSkeleton />)} />
          <Route path="/splits" element={authedLayout(<SplitsPage user={user} />, <GenericPageSkeleton />)} />

          <Route
            path="/service-provider/onboard"
            element={isAuthenticated
              ? withSuspense(<ServiceProviderOnboarding />)
              : <Navigate to="/auth" replace />}
          />

          <Route path="/trips" element={authedLayout(<TripsPage user={user} />, <TripsSkeleton />)} />
          <Route path="/connections" element={authedLayout(<ConnectionsPage user={user} />)} />
          <Route path="/map" element={authedLayout(<MapPage user={user} />)} />
          <Route path="/ai-planner" element={authedLayout(<AIPlannerPage user={user} />)} />
          <Route path="/explore" element={authedLayout(<ExplorePage user={user} />)} />
          <Route path="/explore/:destination" element={authedLayout(<DestinationPage user={user} />)} />
          <Route path="/nearby" element={authedLayout(<NearbyPage user={user} />)} />

          <Route
            path="/planner"
            element={isAuthenticated
              ? withSuspense(<CreateTripWizard user={user} />)
              : <Navigate to="/auth" replace />}
          />

          {/* Alias used by AI Planner "Create Trip" button */}
          <Route
            path="/trips/create"
            element={isAuthenticated
              ? withSuspense(<CreateTripWizard user={user} />)
              : <Navigate to="/auth" replace />}
          />

          <Route
            path="/trip/:tripId"
            element={isAuthenticated
              ? withSuspense(<TripDetailPage user={user} />)
              : <Navigate to="/auth" replace />}
          />

          <Route
            path="/public-trips"
            element={isAuthenticated
              ? withSuspense(<><PublicTripsPage /><BottomNav /></>)
              : <Navigate to="/auth" replace />}
          />

          <Route
            path="/profile"
            element={isAuthenticated
              ? withSuspense(<><ProfilePage user={user} onLogout={handleLogout} /><BottomNav /></>, <ProfileSkeleton />)
              : <Navigate to="/auth" replace />}
          />

          <Route path="/profile/:userId" element={authedLayout(<PublicProfilePage user={user} />, <ProfileSkeleton />)} />

          <Route
            path="/chat/:connectionId"
            element={isAuthenticated
              ? withSuspense(<ConnectionChatPage user={user} />, <ChatRoomSkeleton />)
              : <Navigate to="/auth" replace />}
          />

          <Route
            path="/messages"
            element={isAuthenticated
              ? authedLayout(<MessagesPage user={user} />, <ChatListSkeleton />)
              : <Navigate to="/auth" replace />}
          />

          <Route path="/privacy-policy" element={withSuspense(<PrivacyPolicyPage />)} />
          <Route path="/terms-of-service" element={withSuspense(<TermsOfServicePage />)} />

          <Route path="/admin/login" element={withSuspense(<AdminLogin />)} />
          <Route path="/admin/dashboard" element={withSuspense(<AdminDashboard />)} />

          <Route path="/notifications" element={authedLayout(<NotificationsPage user={user} onRefresh={refreshNotifications} />, <NotificationsSkeleton />)} />

          <Route path="/services/:serviceId" element={authedLayout(<ServiceDetailPage user={user} />)} />
          <Route path="/service/:serviceId" element={authedLayout(<ServiceDetailPage user={user} />)} />
          <Route path="/experiences/:productCode" element={authedLayout(<ExperienceDetailPage user={user} />)} />
          <Route path="/trips/public/:tripId" element={authedLayout(<PublicTripDetailPage user={user} />)} />
          <Route path="/bookings" element={authedLayout(<BookingManagementPage user={user} />)} />
          <Route path="/service-provider-dashboard" element={authedLayout(<ServiceProviderDashboard user={user} />)} />
          <Route path="/invitations" element={authedLayout(<InvitationsPage user={user} />)} />
          <Route path="/trips/:tripId/activities" element={authedLayout(<TripActivitiesPage user={user} />)} />
        </Routes>
      </Router>
      <Toaster position="top-right" />
      <CookieConsentBanner />
    </div>
  );
}

export default App;
